import { type MembroListaElettorale } from '@framework/api-client';
import {
  ConfigFormItem,
  getWidgetProps,
  RichTextAdvancedRender,
  useWidgetScopeContext,
  type IWidgetComponentProps,
} from '@framework/engine';
import { StaticInput, useField } from '@framework/form';
import { Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { CandidateWidgetContainer } from './CandidateWidgetContainer';
import { candidateSelectWidgetObjectName } from './createCandidateSelectFormSchema';
import { generateCandidateLabel } from './form/generateCandidateLabel';
import { type ICandidateWidget } from './ICandidateWidget';
import { CandidateWidgetModal } from './table/CandidateWidgetModal';

export interface ICandidateWidgetComponentProps
  extends IWidgetComponentProps<ICandidateWidget> {}

export const FullWidthCompact = styled(Space.Compact)`
  width: 100%;
`;

/**
 * Widget per la selezione del candidato
 */
export function CandidateWidgetComponent(
  props: ICandidateWidgetComponentProps,
) {
  const { widget } = props;

  const options = getWidgetProps(props);
  const { prefix } = useWidgetScopeContext();

  const objectFieldName = useMemo(
    () => `${prefix}${candidateSelectWidgetObjectName(widget.code)}`,
    [prefix, widget.code],
  );
  const field = useField(options.name);
  const { field: objectField } = useController({ name: objectFieldName });
  const value = useWatch({ name: options.name });

  // Generiamo la stringa da mostrare come valore selezionato
  const labelValue = useMemo(() => {
    if (!objectField?.value || Object.keys(objectField.value).length === 0)
      return '';

    return generateCandidateLabel(objectField.value, widget.labelFieldColumns);
  }, [objectField, widget.labelFieldColumns]);

  const [selected, setSelected] = useState<MembroListaElettorale | null>(value);

  // Alla selezione di un candidato andiamo ad impostare sia il
  // campo principale che quello contentente l'oggetto completo
  const handleSelect = useCallback(() => {
    field.setValue(selected?.id ?? null, {
      shouldValidate: true,
    });
    objectField.onChange(selected ?? null);
  }, [field, objectField, selected]);

  const handleCancel = useCallback(() => {
    setSelected(null);
  }, [setSelected]);

  const handleOpen = useCallback(() => {
    setSelected(objectField.value);
  }, [setSelected, objectField.value]);

  return (
    <CandidateWidgetContainer
      panelTitle={widget.panelTitle}
      showPanel={widget.showPanel}
    >
      <ConfigFormItem
        name={options.name}
        label={<RichTextAdvancedRender inline text={options.label} />}
        help={<RichTextAdvancedRender inline text={options.helpText} />}
        layout={{ hideVisualLabel: options.hideLabel }}
        readonly={options.readonly}
      >
        <FullWidthCompact>
          <StaticInput value={labelValue} placeholder={options.placeholder} />
          <CandidateWidgetModal
            handleSelect={handleSelect}
            handleCancel={handleCancel}
            handleOpen={handleOpen}
            widget={widget}
            setSelected={setSelected}
            currentMember={objectField.value}
            selected={selected}
            readonly={options.readonly}
            fieldName={options.name}
          />
        </FullWidthCompact>
      </ConfigFormItem>
    </CandidateWidgetContainer>
  );
}
